import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api, { BACKEND_URL } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { TokenBoard } from '@/components/TokenBoard';
import { Waves, BookOpen, BarChart3, MessageSquare, Star, PlayCircle, ListChecks, ChevronRight, Send, FileText, Users, Trophy, Droplets, Shield, Award, Download, Share2, X, ClipboardList, Handshake } from 'lucide-react';

export default function ParentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [feedbackForm, setFeedbackForm] = useState({ session_notes: '', engagement_observations: '', suggestions: '' });
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [milestones, setMilestones] = useState(null);
  const [showAchievement, setShowAchievement] = useState(null);

  const selectChild = useCallback(async (child) => {
    setSelectedChild(child);
    try {
      const [sessRes, fbRes, msRes] = await Promise.all([
        api.get(`/sessions?child_id=${child.child_id}`),
        api.get(`/feedback?child_id=${child.child_id}`),
        api.get(`/children/${child.child_id}/milestones`),
      ]);
      setSessions(sessRes.data);
      setFeedbacks(fbRes.data);
      setMilestones(msRes.data);
    } catch (e) { process.env.NODE_ENV === 'development' && console.error(e); }
  }, []);

  useEffect(() => {
    const fetchChildren = async () => {
      try {
        const res = await api.get('/children');
        setChildren(res.data);
        if (res.data.length > 0) {
          selectChild(res.data[0]);
        }
      } catch (e) { process.env.NODE_ENV === 'development' && console.error(e); } finally { setLoading(false); }
    };
    fetchChildren();
  }, [selectChild]);

  const handleSubmitFeedback = async () => {
    if (!selectedChild) return;
    try {
      await api.post('/feedback', {
        child_id: selectedChild.child_id,
        session_id: sessions.length > 0 ? sessions[sessions.length - 1].session_id : null,
        ...feedbackForm
      });
      toast.success('Feedback submitted!');
      setFeedbackForm({ session_notes: '', engagement_observations: '', suggestions: '' });
      const res = await api.get(`/feedback?child_id=${selectedChild.child_id}`);
      setFeedbacks(res.data);
      // Refresh milestones (feedback friend badge)
      try { const msRes = await api.get(`/children/${selectedChild.child_id}/milestones`); setMilestones(msRes.data); } catch (e) { process.env.NODE_ENV === 'development' && console.error(e); }
    } catch { toast.error('Failed to submit feedback'); }
  };

  const MILESTONE_ICONS = { droplets: Droplets, hand: Award, trophy: Trophy, star: Star, 'clipboard-check': ListChecks, mountain: BarChart3, 'message-circle': MessageSquare, shield: Shield };
  const MILESTONE_COLORS = {
    cyan: 'from-cyan-400 to-cyan-600', green: 'from-green-400 to-green-600', amber: 'from-amber-400 to-amber-600',
    yellow: 'from-yellow-400 to-yellow-500', purple: 'from-purple-400 to-purple-600', indigo: 'from-indigo-400 to-indigo-600',
    pink: 'from-pink-400 to-pink-600', blue: 'from-blue-400 to-blue-600',
  };
  const MILESTONE_BG = {
    cyan: 'bg-cyan-50 border-cyan-200', green: 'bg-green-50 border-green-200', amber: 'bg-amber-50 border-amber-200',
    yellow: 'bg-yellow-50 border-yellow-200', purple: 'bg-purple-50 border-purple-200', indigo: 'bg-indigo-50 border-indigo-200',
    pink: 'bg-pink-50 border-pink-200', blue: 'bg-blue-50 border-blue-200',
  };

  const downloadAchievementCard = async (milestone) => {
    try {
      const { default: html2canvas } = await import('html2canvas');
      const el = document.getElementById('achievement-card-capture');
      if (!el) return;
      const canvas = await html2canvas(el, { scale: 2, backgroundColor: null, useCORS: true });
      const link = document.createElement('a');
      link.download = `SplashAble_${selectedChild?.name}_${milestone.title}.png`.replace(/ /g, '_');
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Achievement card downloaded!');
    } catch { toast.error('Failed to download card'); }
  };

  const shareAchievement = async (milestone) => {
    const text = `${selectedChild?.name} earned the "${milestone.title}" badge on SplashAble! ${milestone.description}`;
    if (navigator.share) {
      try { await navigator.share({ title: 'SplashAble Achievement', text }); } catch (e) { process.env.NODE_ENV === 'development' && console.error(e); }
    } else {
      await navigator.clipboard.writeText(text);
      toast.success('Achievement copied to clipboard!');
    }
  };

  if (loading) return <div className="flex items-center justify-center p-12"><img src="/logo.png" alt="Loading" className="w-12 h-12 animate-pulse" /></div>;

  if (children.length === 0) {
    return (
      <div className="text-center py-16" data-testid="no-child-linked">
        <img src="/logo.png" alt="" className="w-16 h-16 mx-auto mb-4 opacity-30" />
        <h2 className="text-2xl font-bold text-slate-400" style={{ fontFamily: 'Nunito' }}>No Child Linked</h2>
        <p className="text-slate-400 mt-2">Ask your instructor to link your account to your child's profile.</p>
      </div>
    );
  }

  const completedSessions = sessions.filter(s => s.status === 'completed');
  const lastSession = sessions[sessions.length - 1];

  return (
    <div className="max-w-5xl mx-auto animate-fadeIn" data-testid="parent-dashboard">
      {/* Child Selector - shown only when multiple children */}
      {children.length > 1 && (
        <div className="mb-6" data-testid="child-selector-tabs">
          <div className="flex gap-3 overflow-x-auto pb-2">
            {children.map(child => (
              <button
                key={child.child_id}
                onClick={() => selectChild(child)}
                className={`flex items-center gap-3 px-5 py-3 rounded-2xl border-2 transition-all flex-shrink-0 ${
                  selectedChild?.child_id === child.child_id
                    ? 'border-[#22D3EE] bg-[#ECFEFF] shadow-md'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
                data-testid={`select-child-tab-${child.child_id}`}
              >
                {child.profile_picture ? (
                  <img src={`${BACKEND_URL}/api/uploads/${child.profile_picture}`} alt={child.name} className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#ECFEFF] to-[#A5F3FC] flex items-center justify-center">
                    <span className="text-lg font-extrabold text-[#0E7490]" style={{ fontFamily: 'Nunito' }}>{child.name.charAt(0)}</span>
                  </div>
                )}
                <div className="text-left">
                  <p className={`font-bold text-sm ${selectedChild?.child_id === child.child_id ? 'text-[#0E7490]' : 'text-slate-700'}`}>{child.name}</p>
                  <p className="text-xs text-slate-400">{child.notes ? child.notes.slice(0, 30) : 'Student'}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedChild && (
        <>
          {/* Book a Meeting — navigates to Schedule */}
          <div
            className="rounded-2xl p-4 mb-6 cursor-pointer transition-all hover:shadow-md bg-[#ECFEFF] border border-[#22D3EE]/30"
            onClick={() => navigate('/dashboard/schedule')}
            data-testid="book-meeting-dashboard-card"
          >
            <div className="flex items-center gap-3">
              <Handshake className="w-6 h-6 text-[#22D3EE] shrink-0" />
              <div className="flex-1">
                <p className="font-bold text-slate-800 text-sm">Book a Meeting</p>
                <p className="text-xs text-slate-500 mt-0.5">Schedule a Meet & Greet, Re-evaluation, or other meeting with your instructor</p>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400" />
            </div>
          </div>

          {/* Token Board */}
          <TokenBoard childId={selectedChild.child_id} childName={selectedChild.name} />

          {/* Welcome */}
          <div className="mb-8 flex items-center gap-4">
            {selectedChild.profile_picture ? (
              <img src={`${BACKEND_URL}/api/uploads/${selectedChild.profile_picture}`} alt={selectedChild.name} className="w-16 h-16 rounded-2xl object-cover border-2 border-[#22D3EE]/30 shadow-md" />
            ) : null}
            <div>
              <h1 className="text-2xl sm:text-4xl font-extrabold text-slate-800 tracking-tight" style={{ fontFamily: 'Nunito' }}>
                {selectedChild.name}'s Portal
              </h1>
              <p className="text-slate-500 mt-1">Track progress, review sessions, and provide feedback</p>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="rounded-2xl border-slate-100 shadow-sm">
              <CardContent className="p-4 text-center">
                <PlayCircle className="w-8 h-8 text-[#22D3EE] mx-auto mb-2" />
                <p className="text-2xl font-extrabold text-slate-800" style={{ fontFamily: 'Nunito' }}>{sessions.length}</p>
                <p className="text-xs text-slate-500 font-medium">Total Sessions</p>
              </CardContent>
            </Card>
            <Card className="rounded-2xl border-slate-100 shadow-sm">
              <CardContent className="p-4 text-center">
                <Star className="w-8 h-8 text-[#C4F017] mx-auto mb-2" />
                <p className="text-2xl font-extrabold text-slate-800" style={{ fontFamily: 'Nunito' }}>{completedSessions.length}</p>
                <p className="text-xs text-slate-500 font-medium">Completed</p>
              </CardContent>
            </Card>
            <Card className="rounded-2xl border-slate-100 shadow-sm">
              <CardContent className="p-4 text-center">
                <BookOpen className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-2xl font-extrabold text-slate-800" style={{ fontFamily: 'Nunito' }}>{feedbacks.length}</p>
                <p className="text-xs text-slate-500 font-medium">Feedbacks</p>
              </CardContent>
            </Card>
            <Card className="rounded-2xl border-slate-100 shadow-sm">
              <CardContent className="p-4 text-center">
                <BarChart3 className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                <p className="text-2xl font-extrabold text-slate-800" style={{ fontFamily: 'Nunito' }}>
                  {lastSession ? `#${lastSession.session_number}` : '-'}
                </p>
                <p className="text-xs text-slate-500 font-medium">Last Session</p>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-8">
            <Button
              data-testid="go-storyboard-btn"
              onClick={() => navigate(`/dashboard/storyboard/${selectedChild.child_id}`)}
              className="h-16 sm:h-20 rounded-2xl bg-gradient-to-r from-[#1E1B4B] to-[#2D2B66] text-white font-bold text-sm sm:text-lg gap-2 shadow-lg hover:shadow-xl transition-shadow"
            >
              <BookOpen className="w-5 h-5" /> Storyboard
            </Button>
            <Button
              data-testid="go-progress-btn"
              onClick={() => navigate(`/dashboard/progress/${selectedChild.child_id}`)}
              variant="outline"
              className="h-16 sm:h-20 rounded-2xl border-2 border-[#22D3EE] text-[#0E7490] font-bold text-sm sm:text-lg gap-2 hover:bg-[#ECFEFF]"
            >
              <BarChart3 className="w-5 h-5" /> Progress
            </Button>
            <Button
              data-testid="go-criteria-btn"
              onClick={() => navigate(`/dashboard/criteria/${selectedChild.child_id}`)}
              variant="outline"
              className="h-16 sm:h-20 rounded-2xl border-2 border-slate-200 text-slate-600 font-bold text-sm sm:text-lg gap-2 hover:bg-slate-50"
            >
              <ListChecks className="w-5 h-5" /> Criteria
            </Button>
            <Button
              data-testid="go-reports-btn"
              onClick={() => navigate(`/dashboard/reports/${selectedChild.child_id}`)}
              variant="outline"
              className="h-16 sm:h-20 rounded-2xl border-2 border-[#C4F017] text-[#1E1B4B] font-bold text-sm sm:text-lg gap-2 hover:bg-[#F7FEE7]"
            >
              <FileText className="w-5 h-5" /> Reports
            </Button>
            <Button
              data-testid="go-intake-btn"
              onClick={() => navigate(`/dashboard/intake/${selectedChild.child_id}`)}
              variant="outline"
              className="h-16 sm:h-20 rounded-2xl border-2 border-purple-200 text-purple-700 font-bold text-sm sm:text-lg gap-2 hover:bg-purple-50 col-span-2 sm:col-span-1"
            >
              <ClipboardList className="w-5 h-5" /> Intake Form
            </Button>
          </div>

          {/* Milestones */}
          {milestones && (
            <Card className="rounded-3xl border-slate-100 shadow-sm mb-8" data-testid="milestones-section">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Nunito' }}>
                    <Trophy className="w-5 h-5 text-amber-500" /> Milestones
                  </CardTitle>
                  <Badge className="rounded-full bg-[#1E1B4B] text-white">
                    {milestones.total_unlocked}/{milestones.total_milestones}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {milestones.milestones.map(m => {
                    const MIcon = MILESTONE_ICONS[m.icon] || Star;
                    const gradient = MILESTONE_COLORS[m.color] || 'from-slate-400 to-slate-600';
                    const bg = MILESTONE_BG[m.color] || 'bg-slate-50 border-slate-200';
                    return (
                      <button
                        key={m.id}
                        onClick={() => m.unlocked && setShowAchievement(m)}
                        className={`relative p-4 rounded-2xl border text-center transition-all ${
                          m.unlocked
                            ? `${bg} hover:shadow-md cursor-pointer`
                            : 'bg-slate-50/50 border-slate-100 opacity-50'
                        }`}
                        data-testid={`milestone-${m.id}`}
                      >
                        <div className={`w-12 h-12 rounded-xl mx-auto mb-2 flex items-center justify-center ${
                          m.unlocked ? `bg-gradient-to-br ${gradient} shadow-md` : 'bg-slate-200'
                        }`}>
                          <MIcon className={`w-6 h-6 ${m.unlocked ? 'text-white' : 'text-slate-400'}`} />
                        </div>
                        <p className={`text-xs font-bold ${m.unlocked ? 'text-slate-800' : 'text-slate-400'}`}>{m.title}</p>
                        {!m.unlocked && m.progress && (
                          <p className="text-[10px] text-slate-400 mt-0.5">{m.progress}</p>
                        )}
                        {m.unlocked && (
                          <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center shadow">
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Achievement Card Dialog */}
          <Dialog open={!!showAchievement} onOpenChange={() => setShowAchievement(null)}>
            <DialogContent className="rounded-3xl max-w-sm p-0 overflow-hidden">
              {showAchievement && (() => {
                const MIcon = MILESTONE_ICONS[showAchievement.icon] || Star;
                const gradient = MILESTONE_COLORS[showAchievement.color] || 'from-slate-400 to-slate-600';
                return (
                  <>
                    {/* Downloadable card area */}
                    <div id="achievement-card-capture" className="bg-white">
                      <div className={`bg-gradient-to-br ${gradient} p-8 text-center`}>
                        <div className="w-20 h-20 rounded-full bg-white/20 mx-auto flex items-center justify-center mb-3 backdrop-blur-sm">
                          <MIcon className="w-10 h-10 text-white" />
                        </div>
                        <h2 className="text-2xl font-extrabold text-white" style={{ fontFamily: 'Nunito' }}>{showAchievement.title}</h2>
                      </div>
                      <div className="p-6 text-center">
                        <p className="text-sm text-slate-600 mb-3">{showAchievement.description}</p>
                        <p className="text-lg font-extrabold text-slate-800" style={{ fontFamily: 'Nunito' }}>{selectedChild?.name}</p>
                        {showAchievement.achieved_at && (
                          <p className="text-xs text-slate-400 mt-1">
                            Achieved {new Date(showAchievement.achieved_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                          </p>
                        )}
                        <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-slate-100">
                          <Waves className="w-4 h-4 text-[#22D3EE]" />
                          <span className="text-xs font-bold text-[#1E1B4B]" style={{ fontFamily: 'Nunito' }}>SplashAble</span>
                          <span className="text-[10px] text-slate-400">by Big Effort Swim</span>
                        </div>
                      </div>
                    </div>
                    {/* Action buttons */}
                    <div className="px-6 pb-6 flex gap-2">
                      <Button
                        onClick={() => downloadAchievementCard(showAchievement)}
                        className="flex-1 rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold gap-2"
                        data-testid="download-achievement-btn"
                      >
                        <Download className="w-4 h-4" /> Download
                      </Button>
                      <Button
                        onClick={() => shareAchievement(showAchievement)}
                        variant="outline"
                        className="flex-1 rounded-full border-[#22D3EE] text-[#0E7490] font-bold gap-2"
                        data-testid="share-achievement-btn"
                      >
                        <Share2 className="w-4 h-4" /> Share
                      </Button>
                    </div>
                  </>
                );
              })()}
            </DialogContent>
          </Dialog>

          {/* Recent Sessions */}
          {sessions.length > 0 && (
            <Card className="rounded-3xl border-slate-100 shadow-sm mb-8" data-testid="recent-sessions">
              <CardHeader><CardTitle style={{ fontFamily: 'Nunito' }}>Recent Sessions</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {sessions.slice(-5).reverse().map(s => (
                  <button
                    key={s.session_id}
                    onClick={() => navigate(`/dashboard/session/${s.session_id}`)}
                    className="w-full flex items-center justify-between bg-slate-50 rounded-2xl p-4 hover:bg-slate-100 transition-colors text-left"
                    data-testid={`parent-session-${s.session_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#ECFEFF] flex items-center justify-center text-[#22D3EE] font-bold">{s.session_number}</div>
                      <div>
                        <p className="font-semibold text-slate-700">Session {s.session_number}</p>
                        <p className="text-xs text-slate-400">{new Date(s.date).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`rounded-full ${s.status === 'completed' ? 'border-green-200 text-green-600 bg-green-50' : 'border-amber-200 text-amber-600 bg-amber-50'}`}>
                        {s.status}
                      </Badge>
                      <ChevronRight className="w-5 h-5 text-[#22D3EE]" />
                    </div>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Feedback Section */}
          <Card className="rounded-3xl border-slate-100 shadow-sm" data-testid="feedback-section">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Nunito' }}>
                <MessageSquare className="w-5 h-5 text-[#22D3EE]" /> Provide Feedback
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="font-semibold text-slate-600">Session Notes</Label>
                <Textarea data-testid="feedback-notes" className="mt-1 rounded-xl" placeholder="How did the session go from your perspective?" value={feedbackForm.session_notes} onChange={e => setFeedbackForm({ ...feedbackForm, session_notes: e.target.value })} />
              </div>
              <div>
                <Label className="font-semibold text-slate-600">Engagement Observations</Label>
                <Textarea data-testid="feedback-engagement" className="mt-1 rounded-xl" placeholder="How engaged was your child? Any observations?" value={feedbackForm.engagement_observations} onChange={e => setFeedbackForm({ ...feedbackForm, engagement_observations: e.target.value })} />
              </div>
              <div>
                <Label className="font-semibold text-slate-600">Suggestions for Future Skills</Label>
                <Textarea data-testid="feedback-suggestions" className="mt-1 rounded-xl" placeholder="Any skills or improvements you'd like to see?" value={feedbackForm.suggestions} onChange={e => setFeedbackForm({ ...feedbackForm, suggestions: e.target.value })} />
              </div>
              <Button data-testid="submit-feedback-btn" onClick={handleSubmitFeedback} className="rounded-full bg-[#22D3EE] hover:bg-[#06B6D4] font-bold gap-2">
                <Send className="w-4 h-4" /> Submit Feedback
              </Button>

              {feedbacks.length > 0 && (
                <div className="mt-6 space-y-3">
                  <h3 className="font-bold text-slate-700" style={{ fontFamily: 'Nunito' }}>Previous Feedback</h3>
                  {feedbacks.slice(0, 5).map(fb => (
                    <div key={fb.feedback_id} className="bg-slate-50 rounded-2xl p-4">
                      <p className="text-xs text-slate-400 mb-2">{new Date(fb.created_at).toLocaleDateString()}</p>
                      {fb.session_notes && <p className="text-sm text-slate-600"><strong>Notes:</strong> {fb.session_notes}</p>}
                      {fb.engagement_observations && <p className="text-sm text-slate-600 mt-1"><strong>Engagement:</strong> {fb.engagement_observations}</p>}
                      {fb.suggestions && <p className="text-sm text-slate-600 mt-1"><strong>Suggestions:</strong> {fb.suggestions}</p>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
